import { useIntl } from 'react-intl';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { CheckboxGroup, type CheckboxItem } from '../CheckboxGroup';

type FilterEntry = { id: string; value: string[] };

type Props = {
  onFilter?: (filters: FilterEntry[]) => void;
  value?: string[];
};

export default function Strategy({ onFilter, value = [] }: Props) {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);

  const options: CheckboxItem[] = [
    {
      id: 'mlo',
      label: formatMessage({
        defaultMessage: 'MLO',
        id: 'campaigns.lineItems.advancedFilters.strategy.mlo'
      }),
      tooltipText: formatMessage({
        defaultMessage:
          'Advanced algorithmic adjustments that optimize bidding strategies across various inventory sources to maximize overall campaign performance.',
        id: 'campaigns.lineItems.advancedFilters.strategy.mlo.tooltip'
      })
    },
    {
      id: 'retargetingWeb',
      label: formatMessage({
        defaultMessage: 'Retargeting Web',
        id: 'campaigns.lineItems.advancedFilters.strategy.retargetingWeb'
      }),
      tooltipText: formatMessage({
        defaultMessage:
          'A strategy that serves ads specifically to users who have previously visited your site, encouraging them to return and complete a conversion.',
        id: 'campaigns.lineItems.advancedFilters.strategy.retargetingWeb.tooltip'
      })
    },
    {
      id: 'deals',
      label: formatMessage({
        defaultMessage: 'Deals',
        id: 'campaigns.lineItems.advancedFilters.strategy.deals'
      }),
      show: checkUserResourcePermissions('deals', user.toJSON(), userRoles),
      tooltipText: formatMessage({
        defaultMessage:
          'Directly negotiate and purchase premium inventory from publishers, ensuring brand safety and access to high-quality audiences.',
        id: 'campaigns.lineItems.advancedFilters.strategy.deals.tooltip'
      })
    }
  ];

  const handleChange = (_id: string, selected: string[]) => {
    onFilter?.([{ id: 'strategy', value: selected }]);
  };

  return (
    <CheckboxGroup
      id="strategy"
      onChange={handleChange}
      options={options}
      selected={value}
      title={formatMessage({
        defaultMessage: 'Strategy & Optimization',
        id: 'campaigns.lineItems.advancedFilters.strategy.title'
      })}
    />
  );
}
